import { apiClient, type ApiResponse, type RequestConfig } from '@shared/api/api-client';
import type {
  AdminPatientNoteDto,
  FlightInformationSummary,
  PatientTagAssignmentDto,
} from '@jema-manager/shared';
import type { PatientDocumentDocType } from '@shared/api/patient-documents';

type GetRequestConfig = Omit<RequestConfig, 'method' | 'body'>;

// Types matching backend
export type QuotingQueueStage =
  | 'quote_accepted'
  | 'collect_docs'
  | 'provider_confirmation'
  | 'trip_planning'
  | 'payment_deposit'
  | 'deposit_paid'
  | 'itinerary_ok'
  | 'pre_arrival'
  | 'ready_travel'
  | 'treatment_ongoing'
  | 'followup_scheduled'
  | 'closed';

export type QuotingQueueSortBy = 'created_at' | 'submitted_at';
export type QuotingQueueSortDirection = 'asc' | 'desc';

export interface QuotingQueueListParams {
  [key: string]: unknown;
  stage?: QuotingQueueStage;
  patientEmail?: string;
  q?: string;
  status?: string;
  hasMedia?: boolean;
  specialty?: string;
  travelCoordinatorUserId?: string;
  sortBy?: QuotingQueueSortBy;
  sortDirection?: QuotingQueueSortDirection;
  limit?: number;
  tagIds?: string[];
}

export interface QuotingQueueCard {
  id: string;
  isLocal?: boolean;
  patientId: string | null;
  currentStage: QuotingQueueStage;
  /** 'automation' | 'manual' | null (unknown / pre-attribution). */
  lastMoveSource?: string | null;
  lastMoveReason?: string | null;
  /** ISO time of the latest stage move (for the why-chip tooltip). */
  lastMovedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  priority: string;
  intake: {
    intakeFormId: string;
    referenceUid: string | null;
    formUid: string | null;
    status?: string | null;
    specialty: string | null;
    hasMedia?: boolean;
    submittedAt?: string | null;
    createdAt?: string | null;
    patientEmail: string | null;
    patientName: string | null;
  };
  acceptedQuote: null | {
    id: string;
    doctorId: string;
    total: number;
    currency: string;
  };
  assignedDoctor: null | {
    doctorId: string;
    displayName: string | null;
    clinicName: string | null;
  };
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  docs: {
    required: number;
    submitted: number;
  };
  flightInformationSummary?: FlightInformationSummary | null;

  travelCase: null | {
    id: string;
    planId: string | null;
    status: 'draft' | 'planned' | 'deposit_pending' | 'deposit_paid' | 'archived' | 'cancelled';
    mergedUsdTotal: number | null;
    travelCoordinatorUserId: string | null;
    travelCoordinator: TravelCoordinatorDisplay | null;
    /** Trip 1 deposit facts (COP); null/absent when unknown. */
    depositRequiredCop?: number | null;
    depositPaidCop?: number | null;
    depositPaidUsd?: number | null;
  };
  tags: PatientTagAssignmentDto[];
  globalTags: PatientTagAssignmentDto[];
  notes?: AdminPatientNoteDto[];
  globalNotes?: AdminPatientNoteDto[];
}

export type TravelCoordinatorState = 'assigned' | 'unassigned' | 'mixed';

export interface TravelCoordinatorDisplay {
  id: string;
  email: string | null;
  name: string | null;
}

export interface QuotingQueueTravelCaseGroup {
  travelCaseId: string;
  planId: string | null;
  status: 'draft' | 'planned' | 'deposit_pending' | 'deposit_paid' | 'archived' | 'cancelled';
  mergedUsdTotal: number | null;
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  travelCoordinatorState: TravelCoordinatorState;
  quoteCount: number;
  serviceRequestIds: string[];
}

export interface QuotingQueuePatientGroup {
  patientKey: string;
  patientId: string | null;
  patientName: string | null;
  patientEmail: string | null;
  tags: PatientTagAssignmentDto[];
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  travelCoordinatorState: TravelCoordinatorState;
  serviceRequests: QuotingQueueCard[];
  travelCases?: QuotingQueueTravelCaseGroup[];
}

export interface PendingQuote {
  id: string;
  doctorId: string;
  doctorName: string | null;
  clinicName: string | null;
  total: number;
  currency: string;
  status: string;
  validUntil: string;
  createdAt: string;
  serviceRequestId: string;
}

export interface PatientServiceRequest {
  id: string;
  currentStage: string;
  specialty: string | null;
  referenceUid: string | null;
  createdAt: string;
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  pendingQuotes: PendingQuote[];
}

export interface PatientDetails {
  patientKey: string;
  patientName: string | null;
  patientEmail: string | null;
  patientPhone: string | null;
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  travelCoordinatorState: TravelCoordinatorState;
  activeServiceRequests: PatientServiceRequest[];
}

export interface ServiceRequestDetails {
  serviceRequest: any;
  travelCoordinatorUserId: string | null;
  travelCoordinator: TravelCoordinatorDisplay | null;
  acceptedQuote: any | null;
  doctor: any | null;
  travelCase?: {
    id: string;
    planId: string | null;
    status: string;
    mergedUsdTotal: number | null;
  } | null;
  documentRequests: any[];
  documentSubmissions: any[];
  flightInformationSummary?: FlightInformationSummary | null;
}

export type TravelCoordinatorAssignmentTarget =
  | { type: 'service-request'; id: string }
  | { type: 'patient-group'; patientKey: string }
  | { type: 'travel-case'; id: string };

export interface TravelCoordinatorAssignmentResult {
  target?: string;
  ids?: string[];
  travelCaseId?: string;
  serviceRequestIds?: string[];
  travelCoordinatorUserId: string | null;
  source: string | null;
}

export interface DocumentRequest {
  id: string;
  service_request_id: string;
  label: string;
  is_required: boolean;
  is_global?: boolean;
  doc_type?: PatientDocumentDocType | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// API Functions
export const quotingQueueApi = {
  /**
   * List all service requests grouped by patient for Kanban
   */
  list: async (
    params?: QuotingQueueListParams,
    config?: GetRequestConfig
  ): Promise<QuotingQueuePatientGroup[]> => {
    const response = await apiClient.get<ApiResponse<QuotingQueuePatientGroup[]>>(
      '/shared/quoting-queue',
      params,
      config
    );
    return response.data;
  },

  /**
   * Get patient details with active SRs and pending quotes
   */
  getPatientDetails: async (
    patientKey: string,
    config?: GetRequestConfig
  ): Promise<PatientDetails> => {
    const response = await apiClient.get<ApiResponse<PatientDetails>>(
      `/shared/quoting-queue/patients/${encodeURIComponent(patientKey)}`,
      undefined,
      config
    );
    return response.data;
  },

  /**
   * Get full service request details
   */
  getServiceRequest: async (
    id: string,
    config?: GetRequestConfig
  ): Promise<ServiceRequestDetails> => {
    const response = await apiClient.get<ApiResponse<ServiceRequestDetails>>(
      `/shared/quoting-queue/${id}`,
      undefined,
      config
    );
    return response.data;
  },

  /**
   * Move service request to different stage
   */
  updateStage: async (
    id: string,
    stage: QuotingQueueStage,
    overrideReason?: string,
    paymentMode?: "installments" | "full"
  ): Promise<{ id: string; currentStage: string; paymentRequest?: "sent" | "failed" | "not_due" }> => {
    const response = await apiClient.patch<ApiResponse<{ id: string; currentStage: string; paymentRequest?: "sent" | "failed" | "not_due" }>>(
      `/shared/quoting-queue/${id}/stage`,
      { stage, ...(overrideReason ? { overrideReason } : {}), ...(paymentMode ? { paymentMode } : {}) }
    );
    return response.data;
  },

  takeOverTravelCoordinator: async (
    target: TravelCoordinatorAssignmentTarget
  ): Promise<TravelCoordinatorAssignmentResult> => {
    const endpoint =
      target.type === 'service-request'
        ? `/shared/quoting-queue/service-requests/${target.id}/takeover`
        : target.type === 'patient-group'
          ? `/shared/quoting-queue/patients/${encodeURIComponent(target.patientKey)}/takeover`
          : `/shared/quoting-queue/travel-cases/${target.id}/takeover`;
    const response = await apiClient.post<ApiResponse<TravelCoordinatorAssignmentResult>>(
      endpoint,
      {}
    );
    return response.data;
  },

  reassignTravelCoordinator: async (
    target: TravelCoordinatorAssignmentTarget,
    travelCoordinatorUserId: string | null
  ): Promise<TravelCoordinatorAssignmentResult> => {
    const endpoint =
      target.type === 'service-request'
        ? `/shared/quoting-queue/service-requests/${target.id}/travel-coordinator`
        : target.type === 'patient-group'
          ? `/shared/quoting-queue/patients/${encodeURIComponent(target.patientKey)}/travel-coordinator`
          : `/shared/quoting-queue/travel-cases/${target.id}/travel-coordinator`;
    const response = await apiClient.patch<ApiResponse<TravelCoordinatorAssignmentResult>>(
      endpoint,
      { travelCoordinatorUserId }
    );
    return response.data;
  },

  /**
   * List document requests for a service request
   */
  listDocRequests: async (
    serviceRequestId: string,
    config?: GetRequestConfig
  ): Promise<DocumentRequest[]> => {
    const response = await apiClient.get<ApiResponse<DocumentRequest[]>>(
      `/shared/quoting-queue/${serviceRequestId}/docs`,
      undefined,
      config
    );
    return response.data;
  },

  /**
   * Create a document request
   */
  createDocRequest: async (
    serviceRequestId: string,
    payload: {
      label: string;
      is_required: boolean;
      is_global?: boolean;
      doc_type?: PatientDocumentDocType;
      sort_order?: number;
    }
  ): Promise<DocumentRequest> => {
    const response = await apiClient.post<ApiResponse<DocumentRequest>>(
      `/shared/quoting-queue/${serviceRequestId}/docs`,
      payload
    );
    return response.data;
  },

  /**
   * Send docs collection link to patient
   */
  sendDocsLink: async (serviceRequestId: string): Promise<{ token: string; url: string }> => {
    const response = await apiClient.post<ApiResponse<{ token: string; url: string }>>(
      `/shared/quoting-queue/${serviceRequestId}/docs/send-link`,
      {}
    );
    return response.data;
  },
};
